'use client'

import { useClerk } from '@clerk/nextjs'
import { nanoid } from 'nanoid'
import { useCallback, useState } from 'react'
import type { PromptInputMessage } from '@/components/ai-elements/prompt-input'
import { ChatPromptInputBar } from '@/components/chat/chat-prompt-input-bar'
import {
  buildPublicComposerDraft,
  writePublicComposerDraft,
} from '@/components/chat/public-composer-draft'
import { publicFlowTemplatePath } from '@/lib/flow-templates/slug'
import { buildFlowRemixPlaceholder } from './flow-remix-prompt'

/**
 * The signed-out Remix box on a flow page. Mirrors the single-template
 * box: the text survives the sign-in round trip as a public composer draft
 * tagged with this flow's slug, and Clerk sends the user back to the
 * signed-in flow page, whose launcher restores the draft for review.
 */
export type PublicFlowRemixPromptBoxProps = {
  slug: string
  brandName: string
  emailCount: number
}

export function PublicFlowRemixPromptBox({
  slug,
  brandName,
  emailCount,
}: PublicFlowRemixPromptBoxProps) {
  const [input, setInput] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { openSignIn } = useClerk()

  const handleFetchChatMentions = useCallback(
    async () => Promise.resolve([]),
    []
  )

  const handleSubmit = useCallback(
    async (
      message: PromptInputMessage,
      event: React.FormEvent<HTMLFormElement>
    ) => {
      event.preventDefault()
      if (isSubmitting) {
        return
      }
      setIsSubmitting(true)
      try {
        const text = message.text?.trim() ?? ''
        if (text) {
          writePublicComposerDraft(
            buildPublicComposerDraft({
              chatId: nanoid(),
              text,
              customBrandId: null,
              files: [],
              remixSourceFlowSlug: slug,
            })
          )
        }
        openSignIn({ forceRedirectUrl: publicFlowTemplatePath(slug) })
      } finally {
        setIsSubmitting(false)
      }
    },
    [isSubmitting, openSignIn, slug]
  )

  return (
    <ChatPromptInputBar
      boxOnly
      enableAutocomplete={false}
      handleFetchChatMentions={handleFetchChatMentions}
      initialOrgId=""
      input={input}
      isLoading={isSubmitting}
      onChangeInput={setInput}
      onSubmit={handleSubmit}
      placeholder={buildFlowRemixPlaceholder({ brandName, emailCount })}
      resume={false}
      status="ready"
    />
  )
}
